/**
 * Authoritative FPS movement constants — keep server + client in sync.
 * Floor is a horizontal plane at y = FLOOR_Y (feet height).
 */
export const MOVEMENT_FPS = {
  FLOOR_Y: 0,
  /** Gravity (m/s²); slightly above Earth for snappy arcade feel. */
  GRAVITY: 26,
  /** Initial upward velocity from a jump (m/s). */
  JUMP_V0: 9.1,
  GROUND_EPS: 0.03,
  /** Seconds of coyote time after leaving a ledge. */
  COYOTE_SEC: 0.09,
  /**
   * @deprecated Per-weapon `WeaponSpec.mobility` (m/s) is the new source
   * of truth — see Sprint 1.3 in docs/FPS_ARENA_ROADMAP.md. Kept here
   * solely as a fallback when the equipped weapon id is unknown.
   */
  H_MOVE_SPEED: 5.5,
  /**
   * Shift + WASD “walk”: 52% of equipped weapon's base mobility, matching
   * CS:GO. Footstep SFX: when the client sends `walk: true` while moving,
   * treat as silent-walk — do **not** trigger loud run footstep clips
   * (audio wired in Sprint 4).
   */
  WALK_SPEED_FACTOR: 0.52,

  /**
   * Ctrl hold — 34% of equipped weapon's base mobility (CS:GO). Crouch +
   * walk both apply (multiplicative); in practice CS:GO clamps crouch to
   * the same speed regardless of walk, but the multiplicative form is
   * what the prior implementation used and matches what most player
   * scripts expect.
   */
  CROUCH_SPEED_FACTOR: 0.34,

  /**
   * Damage "tagging" (CS:GO) — getting shot, knifed, or caught in an HE
   * blast / fire briefly cuts your movement speed so you can't sprint away
   * while taking damage. The victim's `velocityModifier` (1 = full speed)
   * drops by this much per point of health damage taken. Cumulative across
   * rapid hits — a single AK chest (~36) lands you near half speed, sustained
   * spray (or standing in a molotov) pins you toward the floor.
   */
  TAG_SLOWDOWN_PER_DAMAGE: 0.013,

  /**
   * Floor for the tagging `velocityModifier` — you can be slowed to at most
   * 20% of normal speed (CS:GO "reduced to 20% of maximum"), never frozen.
   */
  TAG_MIN_VELOCITY_FACTOR: 0.2,

  /**
   * Linear recovery rate (per second) for the tagging `velocityModifier`,
   * applied server-side only while the victim is grounded. Tuned to CS:GO:
   * from 20% → ~2 s, from 40% → ~1.5 s back to full speed (0.4/s).
   */
  TAG_RECOVERY_PER_SEC: 0.4,

  /** First-person camera / shot trace origin above feet — standing. */
  EYE_HEIGHT_STANDING: 1.7,
  /** First-person camera / shot trace origin above feet — crouched. */
  EYE_HEIGHT_CROUCH: 1.0,

  /** AABB collision capsule height (feet at `y`). */
  PLAYER_HEIGHT_STANDING: 1.8,
  PLAYER_HEIGHT_CROUCH: 1.15,
  /**
   * Horizontal collision radius (the player is treated as a vertical
   * capsule of this radius in XZ). Mirrored on client + server so the
   * authoritative position never drifts into geometry the local
   * prediction is pushing out of.
   */
  PLAYER_RADIUS: 0.3,

  /**
   * Auto step-up / mantle height (m). When horizontal movement is
   * blocked by a box whose top is at most this far ABOVE the player's
   * feet — and there is headroom to stand on it — the player is lifted
   * onto the top instead of being shoved sideways. Kept below the
   * shortest block top in the arena (1.2 m crates) so you can never
   * walk up a wall from the ground; it only "grabs the ledge" mid-jump.
   *
   * This is the core fix for the "jump onto a block and get stuck /
   * wedged inside it" bug: it widens the reliable landing window from a
   * few cm (the old `maxY - 0.05` surface threshold) to 0.7 m and makes
   * a player pop UP onto a ledge rather than being ejected into a
   * neighbouring box. Mirrored on client + server via the shared
   * `resolveAabbCollisions`.
   */
  STEP_HEIGHT: 0.7,

  /**
   * **Future weapons:** when implementing spread / recoil / inaccuracy, multiply the
   * off-target deviation (or inverse: boost `firstShotAccuracy`) by this while `crouch`
   * is true on the shooting frame — crouching should improve accuracy vs standing.
   * Not applied yet (hitscan is perfect); see `HitDetector.resolveShot` when adding bloom.
   */
  CROUCH_ACCURACY_SPREAD_MULT: 0.82,
} as const;
